import React, { useState, useEffect, useCallback } from "react";
import { Group, Badge, ActionIcon, Text, Button } from "@mantine/core";
import { MdRefresh } from "react-icons/md";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from "recharts";
import { getToken, useLogin } from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { getUserActivityAnalyticsApi, UserActivityAnalyticsData } from "../../api";
import {
    ChartScaffold,
    DateRangeControl,
    CountryFilterControl,
    DomainFilterControl,
    TouchpointFilterControl,
    createDefaultDateRange,
    formatDateRange
} from "./shared";
import { TimezoneUtils } from "../../utils/timezoneUtils";
import { TimezoneIndicator } from "../common/TimezoneIndicator";

// Extended interface for chart data with formatted name
interface UserActivityChartData extends UserActivityAnalyticsData {
    formatted_name: string;
}

export interface UserActivityChartProps {
    title?: string;
    height?: number;
    showTitle?: boolean;
    showLegend?: boolean;
    showStats?: boolean;
    defaultTopUsers?: number;
    entraOid?: string; // Filter for specific user
    defaultDaysBack?: number; // Number of days back for default date range
    colors?: {
        total?: string;
    };
}

export const UserActivityChart: React.FC<UserActivityChartProps> = ({
    title = "User Activity Analytics",
    height = 400,
    showTitle = true,
    showLegend = true,
    showStats = true,
    defaultTopUsers = 10,
    entraOid,
    defaultDaysBack = 7,
    colors = {
        total: "#339af0"
    }
}) => {
    const [data, setData] = useState<UserActivityChartData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [availableCountries, setAvailableCountries] = useState<string[]>([]);
    const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
    const [availableDomains, setAvailableDomains] = useState<string[]>([]);
    const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
    const [availableTouchpoints, setAvailableTouchpoints] = useState<string[]>([]);
    const [selectedTouchpoints, setSelectedTouchpoints] = useState<string[]>([]);
    const [topUsers, setTopUsers] = useState(defaultTopUsers);
    const [dateRange, setDateRange] = useState<[Date, Date]>(() => createDefaultDateRange(defaultDaysBack));
    const [totalUsers, setTotalUsers] = useState(0);
    const [sqlQuery, setSqlQuery] = useState<string | undefined>();

    // Authentication
    const client = useLogin ? useMsal().instance : undefined;

    const formatUserName = (name: string, username: string): string => {
        if (name && name !== "Unknown User") {
            return name;
        }
        return username || "Unknown User";
    };

    const loadData = useCallback(async () => {
        if (!client) return;

        setIsLoading(true);
        setError(null);
        try {
            const idToken = await getToken(client);
            if (!idToken) {
                throw new Error("No idToken available for fetching analytics");
            }

            const [startDate, endDate] = dateRange;
            const startDateISO = startDate.toISOString();
            const endDateISO = endDate.toISOString();
            const countries = selectedCountries.length > 0 ? selectedCountries : undefined;
            const domains = selectedDomains.length > 0 ? selectedDomains : undefined;
            const touchpoints = selectedTouchpoints.length > 0 ? selectedTouchpoints : undefined;

            const response = await getUserActivityAnalyticsApi(idToken, topUsers, startDateISO, endDateISO, countries, touchpoints, entraOid, domains);

            // Add formatted names to data for proper chart display and sort by total_messages
            const formattedData = response.data
                .map(user => ({
                    ...user,
                    formatted_name: formatUserName(user.name, user.username)
                }))
                .sort((a, b) => b.total_messages - a.total_messages);

            setData(formattedData);
            setTotalUsers(response.total_users);
            setSqlQuery(response.sql_query);

            // Update available countries and touchpoints from the analytics response
            if (response.available_countries) {
                setAvailableCountries(response.available_countries);
            }
            setAvailableDomains(response.available_domains || []);
            if (response.available_touchpoints) {
                setAvailableTouchpoints(response.available_touchpoints);
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : "Failed to load user activity data");
            setData([]);
            setSqlQuery(undefined);
        } finally {
            setIsLoading(false);
        }
    }, [client, dateRange, selectedCountries, selectedDomains, selectedTouchpoints, topUsers, entraOid]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Calculate stats
    const totalMessages = data.reduce((sum, user) => sum + user.total_messages, 0);
    const totalConversations = data.reduce((sum, user) => sum + user.total_conversations, 0);

    const stats =
        showStats && totalMessages > 0 ? (
            <Group gap="xs">
                <Badge color="blue" variant="light" size="sm">
                    {totalMessages} Total Messages
                </Badge>
                <Badge color="green" variant="light" size="sm">
                    {totalConversations} Total Conversations
                </Badge>
                <Badge color="gray" variant="light" size="sm">
                    {totalUsers} Total Users
                </Badge>
            </Group>
        ) : null;

    const controls = (
        <Group gap={6} style={{ flexWrap: "nowrap" }}>
            <Group gap={2}>
                {[5, 10, 15, 20].map(num => (
                    <Button
                        key={num}
                        variant={topUsers === num ? "filled" : "light"}
                        size="compact-xs"
                        onClick={() => setTopUsers(num)}
                        disabled={!!error}
                        style={{ minWidth: 24 }}
                    >
                        {num}
                    </Button>
                ))}
            </Group>
            <DateRangeControl value={dateRange} onChange={setDateRange} disabled={!!error} />
            <CountryFilterControl
                value={selectedCountries}
                onChange={setSelectedCountries}
                availableCountries={availableCountries}
                disabled={!!error}
                loading={isLoading}
            />
            <DomainFilterControl
                value={selectedDomains}
                onChange={setSelectedDomains}
                availableDomains={availableDomains}
                disabled={!!error}
                loading={isLoading}
            />
            <TouchpointFilterControl
                value={selectedTouchpoints}
                onChange={setSelectedTouchpoints}
                availableTouchpoints={availableTouchpoints}
                disabled={!!error}
                loading={isLoading}
            />
            <ActionIcon variant="light" size="sm" onClick={loadData} loading={isLoading} title="Refresh data" disabled={!!error}>
                <MdRefresh size={16} />
            </ActionIcon>
        </Group>
    );

    // Check if any filters are applied (different from default state)
    const defaultDateRange = createDefaultDateRange(defaultDaysBack);
    const isDateRangeModified = dateRange[0].getTime() !== defaultDateRange[0].getTime() || dateRange[1].getTime() !== defaultDateRange[1].getTime();
    const hasFiltersApplied =
        topUsers !== defaultTopUsers || isDateRangeModified || selectedCountries.length > 0 || selectedDomains.length > 0 || selectedTouchpoints.length > 0;

    const handleResetFilters = () => {
        setTopUsers(defaultTopUsers);
        setDateRange(createDefaultDateRange(defaultDaysBack));
        setSelectedCountries([]);
        setSelectedDomains([]);
        setSelectedTouchpoints([]);
    };

    const info = (
        <Group justify="flex-end" align="center" gap="xs">
            <Text size="xs" c="dimmed">
                {formatDateRange(dateRange)}
            </Text>
            <TimezoneIndicator size="xs" />
        </Group>
    );

    // Custom tooltip component
    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div style={{ backgroundColor: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: 4, padding: "8px" }}>
                    <Text size="sm" fw={500}>
                        {data.formatted_name}
                    </Text>
                    <Text size="xs">Messages: {data.total_messages}</Text>
                    <Text size="xs">Conversations: {data.total_conversations}</Text>
                </div>
            );
        }
        return null;
    };

    // Dynamically size the chart so dense datasets still have breathing room
    const rowHeight = 42;
    const maxRowsBeforeScroll = 15;
    const totalRows = data.length;
    const contentHeight = Math.max(height, totalRows * rowHeight || height);
    const containerHeight = totalRows > maxRowsBeforeScroll ? Math.max(height, maxRowsBeforeScroll * rowHeight) : contentHeight;

    if (!useLogin) {
        return (
            <ChartScaffold title={title} loading={false} error="Authentication not configured">
                <Text>Login is not configured for this application.</Text>
            </ChartScaffold>
        );
    }

    return (
        <ChartScaffold
            title={title}
            showTitle={showTitle}
            loading={isLoading}
            error={error}
            empty={!error && data.length === 0}
            emptyMessage="No user activity data found for the selected time range."
            stats={stats}
            controls={controls}
            info={info}
            height={containerHeight}
            onRefresh={loadData}
            onResetFilters={handleResetFilters}
            showResetFilters={hasFiltersApplied}
            infoContent="Displays user activity analytics showing the most active users by total messages and conversations. Use filters to analyze specific time periods and regions."
            sqlQuery={sqlQuery}
            chartData={data}
            showDeveloperTools={true}
        >
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={data}
                    layout="vertical"
                    margin={{
                        top: 20,
                        right: 80,
                        left: 150,
                        bottom: 20
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 12 }} domain={[0, "dataMax + 10"]} />
                    <YAxis type="category" dataKey="formatted_name" tick={{ fontSize: 12 }} width={150} interval={0} />
                    <Tooltip content={<CustomTooltip />} />
                    {showLegend && <Legend />}
                    <Bar dataKey="total_messages" fill={colors.total} name="Total Messages" barSize={24} radius={[0, 4, 4, 0]}>
                        <LabelList dataKey="total_messages" position="right" style={{ fill: "#666", fontSize: "11px", fontWeight: "bold" }} />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </ChartScaffold>
    );
};
